import { useState } from 'react';

import { Toggle } from '@flipster/react/design-system/toggle';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import {
  isDatadogConsoleDebugEnabled,
  isSegmentConsoleDebugEnabled,
  isWebsocketConsoleDebugEnabled,
  setDatadogConsoleDebugEnabled,
  setSegmentConsoleDebugEnabled,
  setWebsocketConsoleDebugEnabled,
} from '@flipster/utils/dev-debug';

interface LoggingToggleProps {
  label: string;
  selected: boolean;
  onChange: (next: boolean) => void;
}

const LoggingToggle = ({ label, selected, onChange }: LoggingToggleProps) => (
  <li className={hstack({ py: 4, gap: 8 })}>
    <label className={hstack({ gap: 12 })}>
      <Toggle selected={selected} onChange={() => onChange(!selected)} />
      <span className={css({ textStyle: 'Text/L/Monospace/Strong' })}>{label}</span>
    </label>
  </li>
);

export const Logging = () => {
  const [segmentConsoleDebug, setSegmentConsoleDebug] = useState(isSegmentConsoleDebugEnabled);
  const [datadogConsoleDebug, setDatadogConsoleDebug] = useState(isDatadogConsoleDebugEnabled);
  const [websocketConsoleDebug, setWebsocketConsoleDebug] = useState(isWebsocketConsoleDebugEnabled);

  return (
    <div className={stack({ gap: 16, p: 8 })}>
      <h2 className={css({ textStyle: 'Title/XS' })}>Logging</h2>
      <ul className={css({ p: 8, rounded: 4, bgColor: 'background.default.level-0' })}>
        <LoggingToggle
          label='Segment console debug'
          selected={segmentConsoleDebug}
          onChange={(next) => {
            setSegmentConsoleDebugEnabled(next);
            setSegmentConsoleDebug(next);
          }}
        />
        <LoggingToggle
          label='Datadog console debug'
          selected={datadogConsoleDebug}
          onChange={(next) => {
            setDatadogConsoleDebugEnabled(next);
            setDatadogConsoleDebug(next);
          }}
        />
        <LoggingToggle
          label='WebSocket console debug'
          selected={websocketConsoleDebug}
          onChange={(next) => {
            setWebsocketConsoleDebugEnabled(next);
            setWebsocketConsoleDebug(next);
          }}
        />
      </ul>
      <div className={stack({ gap: 8, textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
        <p>
          Segment: prints page and track events to the console. Local environments still do not send events to Segment
          servers.
        </p>
        <p>Datadog: mirrors trade and websocket logger output to the console. Datadog servers are unaffected.</p>
        <p>WebSocket: prints socket state changes, RTT, and reconnect warnings.</p>
      </div>
    </div>
  );
};
